import { useCallback, useEffect, useRef, useState } from "react";
import { HistoryItem } from "../types";

interface UseHistoryPaginationProps {
  fetchHistory: (limit: number, offset?: number) => Promise<HistoryItem[]>;
  limit?: number;
  onNewItemSelected?: (item: HistoryItem) => void;
  initialItems?: HistoryItem[];
  preloadedData?: HistoryItem[];
}

export function useHistoryPagination({ fetchHistory, limit = 50, onNewItemSelected, initialItems = [], preloadedData = [] }: UseHistoryPaginationProps) {
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(initialItems.length === 0 || initialItems.length === limit);
  const [selectedItem, setSelectedItem] = useState<HistoryItem | null>(initialItems.length > 0 ? initialItems[0] : null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasError, setHasError] = useState(false);
  const itemsRef = useRef<HistoryItem[]>(initialItems);
  const lastProcessedPreloadedDataRef = useRef<HistoryItem[]>([]);

  // Effect to handle onNewItemSelected calls after state updates
  useEffect(() => {
    if (selectedItem && onNewItemSelected) {
      onNewItemSelected(selectedItem);
    }
  }, [selectedItem, onNewItemSelected]);

  const loadInitialData = useCallback(async () => {
    if (isLoading || hasError) return; // Prevent multiple simultaneous loads or retries after error

    // Skip loading if we already have initial items
    if (initialItems.length > 0) {
      return;
    }

    setIsLoading(true);
    setHasError(false);
    try {
      const fetchedItems = await fetchHistory(limit);
      setItems(fetchedItems);
      itemsRef.current = fetchedItems;
      setHasMore(fetchedItems.length === limit);

      // Select the first item by default
      if (fetchedItems.length > 0 && !selectedItem) {
        setSelectedItem(fetchedItems[0]);
      }
    } catch (error) {
      setHasMore(false);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [fetchHistory, limit, selectedItem, isLoading, hasError, initialItems]);

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoadingMore || isLoading || hasError) {
      return;
    }

    setIsLoadingMore(true);
    try {
      const newItems = await fetchHistory(limit, items.length);

      // If we get fewer items than the limit, we've reached the end
      if (newItems.length < limit) {
        setHasMore(false);
      }

      if (newItems.length > 0) {
        setItems((prev) => {
          const existingIds = new Set(prev.map((item) => item.id));
          const uniqueNewItems = newItems.filter((item) => !existingIds.has(item.id));

          if (uniqueNewItems.length > 0) {
            // Check if there are new items that should be selected
            const firstNewItem = uniqueNewItems[0];
            if (firstNewItem && !selectedItem) {
              setSelectedItem(firstNewItem);
            }

            const newItemsList = [...prev, ...uniqueNewItems];
            itemsRef.current = newItemsList;
            return newItemsList;
          }

          return prev;
        });
      } else {
        // No more items to load
        setHasMore(false);
      }
    } catch (error) {
      setHasMore(false); // Stop trying on error
      setHasError(true);
    } finally {
      setIsLoadingMore(false);
    }
  }, [fetchHistory, limit, items.length, hasMore, isLoadingMore, isLoading, hasError, selectedItem]);

  const selectItem = useCallback((item: HistoryItem) => {
    setItems((prev) => {
      const exists = prev.some((i) => i.id === item.id);
      if (exists) {
        setSelectedItem(item);
      }
      return prev;
    });
  }, []);

  const checkForNewItems = useCallback(async () => {
    if (isLoading || isLoadingMore || hasError) return; // Prevent checking while loading or after error

    try {
      const newItems = await fetchHistory(limit, 0);
      if (newItems.length > 0) {
        setItems((prev) => {
          const existingIds = new Set(prev.map((item) => item.id));
          const uniqueNewItems = newItems.filter((item) => !existingIds.has(item.id));

          if (uniqueNewItems.length > 0) {
            const newItemsList = [...uniqueNewItems, ...prev];
            itemsRef.current = newItemsList;

            // Always select the newest item when new items are added
            const newestItem = uniqueNewItems[0];
            setSelectedItem(newestItem);

            return newItemsList;
          }

          return prev;
        });
      }
    } catch (error) {
      setHasError(true);
    }
  }, [fetchHistory, limit, isLoading, isLoadingMore, hasError]);

  const refreshData = useCallback(async () => {
    if (isLoading || isLoadingMore || hasError) return;

    try {
      // Fetch fresh data to update favorites and other changes
      const currentItemCount = items.length;
      const refreshedItems = await fetchHistory(currentItemCount || limit, 0);

      if (refreshedItems.length > 0) {
        setItems(refreshedItems);
        itemsRef.current = refreshedItems;

        // Maintain the selected item if it still exists
        if (selectedItem) {
          const updatedSelectedItem = refreshedItems.find((item) => item.id === selectedItem.id);
          if (updatedSelectedItem) {
            setSelectedItem(updatedSelectedItem);
            onNewItemSelected?.(updatedSelectedItem);
          }
        }
      }
    } catch (error) {
      setHasError(true);
    }
  }, [fetchHistory, limit, items.length, selectedItem, onNewItemSelected, isLoading, isLoadingMore, hasError]);

  const resetPagination = useCallback(() => {
    setItems([]);
    setSelectedItem(null);
    setIsLoading(false);
    setIsLoadingMore(false);
    setHasMore(true);
    setHasError(false);
    itemsRef.current = [];
  }, []);

  // Handle preloaded data changes
  useEffect(() => {
    if (preloadedData && preloadedData.length > 0) {
      // Check if this is the same data we already processed
      const lastProcessedFirstId = lastProcessedPreloadedDataRef.current[0]?.id;
      const currentPreloadedFirstId = preloadedData[0]?.id;

      if (lastProcessedFirstId === currentPreloadedFirstId && lastProcessedPreloadedDataRef.current.length === preloadedData.length) {
        return;
      }

      // Update the last processed data reference
      lastProcessedPreloadedDataRef.current = preloadedData;

      if (items.length === 0) {
        // Initial load: apply preloaded data directly
        setItems(preloadedData);
        itemsRef.current = preloadedData;
        setHasMore(preloadedData.length === limit);

        // Select the first item by default
        if (!selectedItem) {
          setSelectedItem(preloadedData[0]);
          onNewItemSelected?.(preloadedData[0]);
        }
      } else {
        // Check if the first item changed (indicates fresh data)
        const currentFirstId = items[0]?.id;
        const preloadedFirstId = preloadedData[0]?.id;

        if (currentFirstId !== preloadedFirstId) {
          // Replace all items with fresh data
          setItems(preloadedData);
          itemsRef.current = preloadedData;
          setHasMore(preloadedData.length === limit);

          // Select the first (newest) item
          setSelectedItem(preloadedData[0]);
          onNewItemSelected?.(preloadedData[0]);
        } else {
          // We already have items, check for new ones and merge them
          const existingIds = new Set(items.map((item) => item.id));
          const uniqueNewItems = preloadedData.filter((item) => !existingIds.has(item.id));

          if (uniqueNewItems.length > 0) {
            setItems((prev) => {
              const newItemsList = [...uniqueNewItems, ...prev];
              itemsRef.current = newItemsList;

              // Always select the newest item when new items are added
              const newestItem = uniqueNewItems[0];
              setSelectedItem(newestItem);
              onNewItemSelected?.(newestItem);

              return newItemsList;
            });
          }
        }
      }
    }
  }, [preloadedData, limit]);

  const updateSelectedItemFavorite = useCallback((snapshotId: string, isFavorite: boolean) => {
    // Update the selected item if it matches
    if (selectedItem && selectedItem.id === snapshotId) {
      const updatedSelectedItem = {
        ...selectedItem,
        isFavorite,
        data: {
          ...selectedItem.data,
          isFavorite
        }
      };
      setSelectedItem(updatedSelectedItem);
      onNewItemSelected?.(updatedSelectedItem);
    }

    // Update the item in the items array
    setItems(prevItems =>
      prevItems.map(item =>
        item.id === snapshotId
          ? { ...item, isFavorite, data: { ...item.data, isFavorite } }
          : item
      )
    );
  }, [selectedItem, onNewItemSelected]);

  return {
    items,
    selectedItem,
    isLoading,
    isLoadingMore,
    hasMore,
    hasError,
    loadInitialData,
    loadMore,
    selectItem,
    checkForNewItems,
    initialLoadComplete: !isLoading && (items.length > 0 || initialItems.length > 0),
    refreshData,
    resetPagination,
    updateSelectedItemFavorite,
  };
}
